import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { Alert, Avatar, Button, Paper, styled, Typography } from '@mui/material'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Box } from '@/components/ui'
import type { OrganizationDetail } from '@/definitions/organization'
import type { ProductFlagFormValues, ProductFlagTier } from '@/definitions/productFlags'
import { useConfirm } from '@/hooks/useConfirm'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useUpdateOrgProductFlags } from '@/hooks/useUpdateOrgProductFlags'
import { borderWidth, spacing } from '@/themes/tokens'
import { getApiErrorMessage } from '@/utils/apiErrors'

import { buildFlagFormValues } from '../../utils/buildFlagFormValues'
import { buildFlagSections } from '../../utils/buildFlagSections'
import { buildProductFlagsRequest } from '../../utils/buildProductFlagsRequest'
import { deriveSelectedTier } from '../../utils/deriveSelectedTier'
import {
  mapIntegrationErrors,
  unmatchedIntegrationErrors,
} from '../../utils/integrationErrors'
import { PRODUCT_FLAG_COUNTS_BY_TIER } from '../../utils/productFlagCountsByTier'
import { summarizeFlagChanges } from '../../utils/summarizeFlagChanges'
import { applyTierSelection } from '../../utils/tierEditability'
import { ProductFlagSection } from '../ProductFlagSection'
import { SaveFlagsConfirmation } from '../SaveFlagsConfirmation'
import { TierSelector } from '../TierSelector'

const SAVE_FAILED_MESSAGE = 'These product flag changes could not be saved.'

const PARTIAL_SUCCESS_MESSAGE =
  'Saved, but some integrations did not apply. See the product flags marked below.'

const SAVE_SUCCEEDED_MESSAGE = 'Product flag changes saved.'

const INTRO = 'Toggle any included product flag on or off for this practice.'

/**
 * The two columns, filling the pane they are given rather than growing past it.
 *
 * Below `md` they stack and the tab scrolls as one page: side by side is what makes
 * a column worth holding still, and a stacked narrow window has no height to spare
 * for it.
 */
const TwoColumnLayout = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.xl),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    height: '100%',
    minHeight: 0,
  },
}))

/**
 * Which package the practice is on, and which sections that leaves editable — the
 * context for every switch in the other column, so it stays put while they scroll.
 * Three cards will not overflow in practice; the scroll is there so a short window
 * cannot put the last package out of reach.
 */
const PlanColumn = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flex: '0 0 33.33%',
    overflowY: 'auto',
  },
}))

const ProductFlagsColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.lg),
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
}))

/**
 * The one region that scrolls.
 *
 * `minHeight: 0` is what makes it the one: without it a flex item refuses to shrink
 * below its content, and a long list would push the save buttons off the bottom
 * instead of scrolling under them.
 */
const FlagList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.lg),
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Keeps the scrollbar off the cards' rounded edge.
    paddingRight: theme.spacing(spacing.md),
  },
}))

/** Saving stays at the foot of the column, with a rule marking what scrolls above it. */
const SaveActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(spacing.md),
  [theme.breakpoints.up('md')]: {
    borderTop: `${borderWidth.thin}px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(spacing.lg),
  },
}))

interface ProductFlagsFormProps {
  org: OrganizationDetail
}

/**
 * Product flag management for one organization.
 *
 * Nothing here writes as it is clicked: the switches stage against the stored values
 * and one save applies the set, because the endpoint replaces the whole collection
 * rather than patching a field. Selecting a lower package stages the product flags
 * above it off, which is a real downgrade, so the save asks first and names what it is
 * about to take away.
 *
 * A save can succeed while a named integration does not apply — enabling a product flag
 * that provisions a third party can fail on its own — so the response is what the form
 * resets to, never the values that were submitted.
 */
export const ProductFlagsForm = ({ org }: ProductFlagsFormProps) => {
  const confirm = useConfirm()
  const { showSuccess, showWarning } = useSnackbar()
  const {
    mutateAsync,
    isPending,
    data: saved,
    error,
    reset: forgetLastSave,
  } = useUpdateOrgProductFlags()

  const methods = useForm<ProductFlagFormValues>({
    defaultValues: buildFlagFormValues(org),
  })
  const { formState, getValues, handleSubmit, reset, watch } = methods

  // Null for an organization on no package, which leaves every package section
  // locked until the operator picks one.
  const [selectedTier, setSelectedTier] = useState<ProductFlagTier | null>(() =>
    deriveSelectedTier(org))

  // Both read off the mutation rather than mirrored into state. It clears them when the
  // next save starts, which is exactly when they stop being true — the same lifecycle a
  // pair of useStates here would have to reproduce by hand.
  const integrationErrors = saved?.integration_errors ?? []
  const saveError =
    error === null ? null : getApiErrorMessage(error, SAVE_FAILED_MESSAGE)

  // What the practice is on according to its saved product flags. Distinct from
  // `selectedTier`, which follows the operator once they start clicking.
  const currentTier = deriveSelectedTier(org)

  // Subscribing to the whole form is what redraws a row when its switch moves.
  const values = watch()
  const errorsByField = mapIntegrationErrors(integrationErrors)
  const orphanErrors = unmatchedIntegrationErrors(integrationErrors)
  // The stored SID rather than the staged one: clearing the field to retype it does not
  // take the organization's Twilio subaccount away, so it must not read as Texting
  // losing what it needs.
  const sections = buildFlagSections(
    values,
    selectedTier,
    org.twilio_account_sid,
    errorsByField
  )

  const handleTierSelect = (tier: ProductFlagTier) => {
    // Choosing a package is an edit, so the staged values change but the defaults
    // stay the stored ones — that is what keeps the save button meaning "differs
    // from what is saved".
    reset(applyTierSelection(getValues(), tier, org.twilio_account_sid), {
      keepDefaultValues: true,
    })
    setSelectedTier(tier)
  }

  const handleReset = () => {
    reset()
    setSelectedTier(currentTier)
    // Returning to the stored values makes the last save's report stale, and the
    // mutation is where that report lives.
    forgetLastSave()
  }

  const onSubmit = handleSubmit(async (staged) => {
    const changes = summarizeFlagChanges(buildFlagFormValues(org), staged)

    const confirmed = await confirm({
      title: 'Save product flag changes?',
      body: <SaveFlagsConfirmation changes={changes} />,
      confirmLabel: 'Save changes',
      destructive: changes.disabling.length > 0,
    })
    if (!confirmed) {
      return
    }

    // A refusal rejects, which is what keeps everything below from running; the alert
    // renders it from the mutation's own error state rather than from here.
    const stored = await mutateAsync({
      orgId: org.id,
      data: buildProductFlagsRequest(staged),
    })

    // The response reports what was actually stored, which is not always what
    // was asked for, so both the form and the package selection follow it.
    reset(buildFlagFormValues(stored))
    setSelectedTier(deriveSelectedTier(stored))

    if (stored.integration_errors.length > 0) {
      showWarning(PARTIAL_SUCCESS_MESSAGE)
    } else {
      showSuccess(SAVE_SUCCEEDED_MESSAGE)
    }
  })

  return (
    <FormProvider {...methods}>
      <TwoColumnLayout>
        <PlanColumn>
          <TierSelector
            selectedTier={selectedTier}
            currentTier={currentTier}
            productFlagCounts={PRODUCT_FLAG_COUNTS_BY_TIER}
            onSelect={handleTierSelect}
          />
        </PlanColumn>

        <ProductFlagsColumn>
          <Box variant='flex' gap='md'>
            <Avatar variant='titleIcon'>
              <TuneOutlinedIcon />
            </Avatar>
            <Box variant='flexColumn' gap='none' fullWidth={false}>
              <Typography variant='h4' fontWeight={600}>
                Product Flags
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {INTRO}
              </Typography>
            </Box>
          </Box>

          {/* Outside the scrolling list on purpose: a save that fails while the
              operator is down among the switches has to be readable from where
              they are standing, not only from the top of the list. */}
          {saveError !== null && <Alert severity='error'>{saveError}</Alert>}

          {orphanErrors.map((error) => (
            <Alert key={error.field} severity='warning'>
              {error.message}
            </Alert>
          ))}

          <FlagList>
            {sections.map((section) => (
              <Paper key={section.tier} variant='detailCard'>
                <ProductFlagSection
                  title={section.title}
                  isEditable={section.isEditable}
                  rows={section.rows}
                />
              </Paper>
            ))}
          </FlagList>

          <SaveActions>
            <Button
              variant='contained'
              disabled={!formState.isDirty || isPending}
              onClick={onSubmit}
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
            {/* There is nowhere to cancel to — this is a tab, not a dialog — so
                the button says what it actually does. */}
            <Button
              variant='outlined'
              disabled={!formState.isDirty || isPending}
              onClick={handleReset}
            >
              Reset to saved
            </Button>
          </SaveActions>
        </ProductFlagsColumn>
      </TwoColumnLayout>
    </FormProvider>
  )
}
